import type { OrganizationUsageStatsDto } from '../../../../../../trustpulse-api/src/application/dto/organization/organization-usage-stats.dto';
import { PercentageCalculator } from './PercentageCalculator';

/**
 * Stat item for display
 */
export interface StatItem {
  label: string;
  value: string | number;
  total?: number;
  icon?: 'people' | 'chat' | 'envelope' | 'calendar';
}

/**
 * Translation function type
 */
export type TranslationFunction = (key: string) => string;

/**
 * Static utility class for transforming usage statistics data for display
 */
export class UsageStatsTransformer {
  /**
   * Transforms organization usage stats into primary stat items
   * @param stats Organization usage statistics
   * @param t Translation function
   * @returns Array of primary stat items
   */
  static transformPrimaryStats(
    stats: OrganizationUsageStatsDto,
    t: TranslationFunction
  ): StatItem[] {
    return [
      {
        label: t('usage.overview.stats.totalTeams'),
        value: stats.totalTeamsCount,
        icon: 'people',
      },
      {
        label: t('usage.overview.stats.totalSurveysSent'),
        value: stats.totalSurveysSent,
        icon: 'envelope',
      },
      {
        label: t('usage.overview.stats.totalResponsesReceived'),
        value: stats.totalResponsesReceived,
        icon: 'chat',
      },
      {
        label: t('usage.overview.stats.averageResponseRate'),
        value: PercentageCalculator.format(stats.averageResponseRate, 1),
        icon: 'calendar',
      },
    ];
  }

  /**
   * Transforms organization usage stats into secondary stat items
   * @param stats Organization usage statistics
   * @param t Translation function
   * @returns Array of secondary stat items
   */
  static transformSecondaryStats(
    stats: OrganizationUsageStatsDto,
    t: TranslationFunction
  ): StatItem[] {
    return [
      {
        label: t('usage.overview.stats.teamsWithActiveSurveys'),
        value: stats.teamsWithActiveSurveysCount,
        total: stats.totalTeamsCount,
      },
      {
        label: t('usage.overview.stats.teamsWithScheduledSurveys'),
        value: stats.teamsWithScheduledSurveysCount,
        total: stats.totalTeamsCount,
      },
      {
        label: t('usage.overview.stats.totalScheduledSurveys'),
        value: stats.totalScheduledSurveys,
      },
      {
        label: t('usage.overview.stats.recurringScheduledSurveys'),
        value: stats.recurringScheduledSurveys,
        total: stats.totalScheduledSurveys,
      },
    ];
  }
}
